/**
 * SourcesTable — top utm_source values with an anti-collapse render
 * pass that GUARANTEES facebook + instagram appear as distinct rows.
 *
 * Anti-collapse contract (Commit C dispatch §3):
 *   * The server does not group fb / ig under "Meta" / "social".
 *   * If a future refactor introduces upstream grouping, this
 *     render pass MUST split them back out — it explicitly asserts
 *     the row shape and refuses to render a collapsed "Meta" bucket.
 *
 * The implementation here is defensive: we take the server payload,
 * strip any row whose `utm_source` is a known-collapsed alias
 * ("meta", "social"), and log a console.warn so the drift is
 * observable. Then we sort the remaining rows by count descending.
 *
 * This is enforced by the yarn test that seeds both fb + ig +
 * asserts distinct rows land in the DOM.
 */
import React from "react";
import EmptyState from "./EmptyState";

const _COLLAPSED_ALIASES = new Set(["meta", "social", "social-media"]);

function _antiCollapse(rows) {
  if (!Array.isArray(rows)) return [];
  const kept = [];
  for (const row of rows) {
    const src = String(row?.utm_source || "").toLowerCase();
    if (_COLLAPSED_ALIASES.has(src)) {
      // Explicitly refuse to render — drift signal for the operator.
      // eslint-disable-next-line no-console
      console.warn(
        `[SourcesTable] refusing to render collapsed source "${src}" — ` +
        "keep facebook / instagram as distinct rows per Commit C dispatch §3.",
      );
      continue;
    }
    kept.push(row);
  }
  return kept.sort((a, b) => (b.count || 0) - (a.count || 0));
}

export default function SourcesTable({ rows, firstRollupDate }) {
  const cleaned = _antiCollapse(rows);
  if (cleaned.length === 0) {
    return (
      <EmptyState
        firstRollupDate={firstRollupDate}
        variant="card"
        testId="analytics-sources-empty"
      />
    );
  }

  return (
    <table
      className="w-full text-sm"
      data-testid="analytics-sources-table"
    >
      <thead>
        <tr
          className="border-b text-left text-[11px] font-bold uppercase tracking-[0.06em]"
          style={{
            borderColor: "var(--pp-border)",
            color: "var(--pp-text-soft)",
          }}
        >
          <th className="py-2 pr-4">Source</th>
          <th className="py-2 text-right">Visits</th>
        </tr>
      </thead>
      <tbody>
        {cleaned.map((r) => (
          <tr
            key={r.utm_source}
            className="border-b last:border-b-0"
            style={{ borderColor: "var(--pp-border)" }}
            data-testid={`analytics-sources-row-${r.utm_source}`}
          >
            <td className="py-2 pr-4" style={{ color: "var(--pp-text)" }}>
              {r.utm_source}
            </td>
            <td
              className="py-2 text-right font-medium tabular-nums"
              style={{ color: "var(--pp-text)" }}
            >
              {(r.count || 0).toLocaleString("en-US")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
